import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import ProfileAction from "./ProfileAction";
import Tab from "./Tab";

import {
  useAuthStore,
  useBusinessUnitStore,
  useLayoutStore,
} from "@type/store";
import { generateTabItems } from "helpers/generateTabItems";
import { getAllowedTabList } from "helpers/getAllowedTabList";
import { isMatchParentPath } from "helpers/isMatchParentPath";
import { useGetMainPathType } from "hooks/useGetMainPathType";
import { useHideTopNavBar } from "hooks/useHideTopNavBar";
import { useRenderStatus } from "hooks/useRenderStatus";
import cn from "lib/cn";

import { ENavTab } from "../types";
import { TAB_LABEL } from "../constants";

import styles from "./Navbar.module.css";

export interface NavbarProps {
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  id?: string;
}

const Navbar = ({ setSidebarCollapsed, id }: NavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const busy = useRenderStatus(500);
  const { buSlug } = useParams();

  const appAccessRight = useBusinessUnitStore((state) => state.appAccessRight);
  const mainPathType = useGetMainPathType();
  const { isHideTopNavBar } = useHideTopNavBar();
  const accessDenied = useLayoutStore((state) => state.accessDenied);
  const isDataNotFound = useLayoutStore((state) => state.isDataNotFound);

  const [selectedTab, setSelectedTab] = useState<ENavTab>();

  const tabItems = useMemo(() => {
    const tabList = getAllowedTabList({
      pathname: location.pathname,
      buAccessRight: appAccessRight,
      isSuperAdmin: user.isSuperAdmin,
    });

    return generateTabItems({
      tabLabel: TAB_LABEL,
      tabList: tabList,
      pathname: location.pathname,
      buSlug: buSlug,
    });
  }, [user, location.pathname, appAccessRight, buSlug]);

  const showNavTab = useMemo(() => {
    return !isHideTopNavBar && !accessDenied && !isDataNotFound && !busy;
  }, [isHideTopNavBar, accessDenied, isDataNotFound, busy]);

  const handleClickLogo = () => {
    navigate("/");
    setSidebarCollapsed(true);
  };

  const handleChangeTab = useCallback(
    (tab: string) => {
      const item = tabItems[tab];
      if (!item.path) return;
      navigate(item.path);
    },
    [navigate, tabItems]
  );

  useEffect(() => {
    let parentTab: ENavTab | undefined = undefined;
    let parentTabLength = 0;

    for (const key in tabItems) {
      const item = tabItems[key];
      if (!item.path) continue;

      if (matchPath({ path: item.path, end: true }, location.pathname)) {
        parentTab = undefined;
        setSelectedTab(key as ENavTab);
        break;
      }

      const currentArrPath = location.pathname
        .split("/")
        .filter((path) => path);
      const arrItemPath = item.path.split("/").filter((path) => path);
      if (
        isMatchParentPath({
          currentPaths: currentArrPath,
          entityType: mainPathType,
          parentPath: item.path,
        })
      ) {
        const allSame = arrItemPath.every((item) =>
          currentArrPath.some((path) => path === item)
        );
        if (
          (currentArrPath?.[currentArrPath.length - 2] ===
            arrItemPath?.[arrItemPath.length - 1] ||
            allSame) &&
          currentArrPath.length >= parentTabLength
        ) {
          parentTab = key as ENavTab;
          parentTabLength = currentArrPath.length;
        }
      }
    }

    if (parentTab) {
      setSelectedTab(parentTab);
    }
  }, [location.pathname, mainPathType, tabItems]);

  return (
    <div
      data-testid={id}
      className="h-[4rem] flex w-full justify-between items-center gap-6 bg-white pl-24 pr-12 border-b border-line-500"
    >
      <div
        className="w-[8.125rem] min-w-[8.125rem] h-full flex items-center justify-start cursor-pointer border-line-500"
        onClick={handleClickLogo}
      >
        <img alt="logo" src="/images/logo.png" width={60} height={35} />
      </div>
      <div className={cn("h-full", styles.tabWrapper)}>
        {showNavTab && (
          <Tab
            selected={selectedTab}
            onChange={handleChangeTab}
            items={tabItems}
          />
        )}
      </div>
      <div className="w-[30rem] min-w-[7.5rem] flex items-center space-x-6 justify-end">
        <ProfileAction />
      </div>
    </div>
  );
};

export default Navbar;
